import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "./api";
import { performFullReload } from "./reload";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=anonymous
  const [admin, setAdmin] = useState(undefined);
  const userId = user?.id;

  const fetchMe = useCallback(async () => {
    window.__m11AuthBootstrapPending = true;
    const token = localStorage.getItem("m11_token");
    const role = localStorage.getItem("m11_role");
    if (!token) {
      setUser(null);
      setAdmin(null);
      window.__m11AuthBootstrapPending = false;
      return;
    }
    // 🔒 INSTANT-BOOT: hydrate UI immediately from cached profile so slow /auth/me
    // network call cannot flash a "logged out" screen on Android WebView cold-start.
    try {
      const cached = localStorage.getItem("m11_user_cache");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (role === "admin") {
          setAdmin(parsed); setUser(null);
        } else {
          setUser(parsed); setAdmin(null);
        }
      }
    } catch { /* ignore corrupt cache */ }

    try {
      if (role === "admin") {
        const { data } = await api.get("/admin/auth/me");
        setAdmin(data);
        setUser(null);
        localStorage.setItem("m11_user_cache", JSON.stringify(data));
      } else {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setAdmin(null);
        localStorage.setItem("m11_user_cache", JSON.stringify(data));
      }
    } catch (e) {
      // 🔒 NEVER auto-logout on ANY error (401, 403, 5xx, network, timeout).
      // Users expect Instagram/Zomato style "always signed in".
      // The user must explicitly tap Logout to clear their session.
      // If they have a cached profile, keep showing it; otherwise show stale marker
      // so UI renders (any real action will re-auth via login flow).
      const cachedRaw = localStorage.getItem("m11_user_cache");
      if (cachedRaw) {
        try {
          const parsed = JSON.parse(cachedRaw);
          if (role === "admin") { setAdmin(parsed); setUser(null); }
          else                   { setUser(parsed); setAdmin(null); }
        } catch { /* corrupt cache — fall through */ }
      } else {
        // No cache: show a stale placeholder so the app shell can render
        setUser((prev) => (role !== "admin") ? (prev || { _stale: true }) : prev);
        setAdmin((prev) => (role === "admin") ? (prev || { _stale: true }) : prev);
      }
    } finally {
      window.__m11AuthBootstrapPending = false;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Track app opens only after a real user session exists, so login never emits a 401.
  useEffect(() => {
    if (!user?.id || user?._stale) return;
    const sessKey = "m11_app_open_fired";
    if (sessionStorage.getItem(sessKey)) return;
    sessionStorage.setItem(sessKey, "1");
    api.post("/analytics/app-open").catch(() => {
      sessionStorage.removeItem(sessKey);
    });
  }, [user?.id, user?._stale]);

  // 🔄 SILENT auto-update when a new build is deployed
  useEffect(() => {
    // Poll /app-version — when it changes, SILENTLY refresh (no banner, no
    // prompt). We only reload when the user is IDLE so we never interrupt a
    // bet, chat message, or form. localStorage is preserved → NO LOGOUT.
    let known = null;
    let pendingReload = false;

    const isBusy = () => {
      // User is "busy" if any input/textarea has focus (typing), OR a
      // Dialog/Modal is open, OR they're currently touching the screen.
      const ae = document.activeElement;
      if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable)) return true;
      if (document.querySelector("[role='dialog'][data-state='open'], .modal-open")) return true;
      return false;
    };

    const doReload = () => {
      if (isBusy()) {
        // User is doing something — try again in 5s
        setTimeout(doReload, 5000);
        return;
      }
      performFullReload();
    };

    const checkVersion = async () => {
      try {
        const { data } = await api.get("/app-version");
        const v = data?.version;
        if (!v) return;
        if (known === null) { known = v; return; }
        if (v !== known && !pendingReload) {
          pendingReload = true;
          doReload();
        }
      } catch { /* offline or backend down — ignore, will retry */ }
    };
    checkVersion();
    const id = setInterval(checkVersion, 30_000); // every 30 seconds
    return () => clearInterval(id);
  }, []);

  const login = async (mobile, password) => {
    const { data } = await api.post("/auth/login", { mobile, password });
    localStorage.setItem("m11_token", data.token);
    localStorage.setItem("m11_role", "user");
    localStorage.setItem("m11_mobile", data.user?.mobile || mobile);
    localStorage.setItem("m11_user_cache", JSON.stringify(data.user));
    // APK bridge: register user with OneSignal for targeted push notifications
    if (typeof window !== "undefined" && window.AndroidBridge?.setUserId) {
      try { window.AndroidBridge.setUserId(String(data.user?.mobile || mobile)); } catch {}
    }
    setUser(data.user);
    setAdmin(null);
    return data.user;
  };

  const register = async (mobile, name, password, referral_code, email) => {
    const { data } = await api.post("/auth/register", { mobile, name, password, referral_code, email });
    localStorage.setItem("m11_token", data.token);
    localStorage.setItem("m11_role", "user");
    localStorage.setItem("m11_mobile", data.user?.mobile || mobile);
    localStorage.setItem("m11_user_cache", JSON.stringify(data.user));
    if (typeof window !== "undefined" && window.AndroidBridge?.setUserId) {
      try { window.AndroidBridge.setUserId(String(data.user?.mobile || mobile)); } catch {}
    }
    setUser(data.user);
    return data.user;
  };

  const setSessionFromResetToken = (token, userData) => {
    localStorage.setItem("m11_token", token);
    localStorage.setItem("m11_role", "user");
    localStorage.setItem("m11_user_cache", JSON.stringify(userData));
    setUser(userData);
    setAdmin(null);
  };

  const adminLogin = async (email, password) => {
    const { data } = await api.post("/admin/auth/login", { email, password });
    localStorage.setItem("m11_token", data.token);
    localStorage.setItem("m11_role", "admin");
    localStorage.setItem("m11_user_cache", JSON.stringify(data.admin));
    // APK bridge: register admin so backend can push new-chat alerts to their device
    if (typeof window !== "undefined" && window.AndroidBridge?.setUserId) {
      try { window.AndroidBridge.setUserId("admin_console"); } catch {}
    }
    // Web Push (OneSignal browser SDK): tie this browser to the "admin_console" external ID
    // so backend push arrives on the installed PWA icon as a badge notification.
    if (typeof window !== "undefined") {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async function (OneSignal) {
        try {
          await OneSignal.login("admin_console");
          // Ask permission for push (needed for PWA badges + notifications)
          if (OneSignal.Notifications && !OneSignal.Notifications.permission) {
            try { await OneSignal.Notifications.requestPermission(); } catch {}
          }
        } catch (e) { /* noop */ }
      });
    }
    setAdmin(data.admin);
    setUser(null);
    return data.admin;
  };

  const logout = () => {
    localStorage.removeItem("m11_token");
    localStorage.removeItem("m11_role");
    localStorage.removeItem("m11_mobile");
    localStorage.removeItem("m11_user_cache");
    if (typeof window !== "undefined" && window.AndroidBridge?.clearUserId) {
      try { window.AndroidBridge.clearUserId(); } catch {}
    }
    setUser(null);
    setAdmin(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // ignore
    }
  };

  // 🔄 REAL-TIME AUTO-REFRESH: poll user profile every 8 seconds so wallet
  // balance, transactions and notifications reflect deposits/wins the moment
  // they land — user NEVER needs to close & reopen the app.
  useEffect(() => {
    if (!userId || admin) return;
    let cancelled = false;
    let pageUnloading = false;
    const tick = async () => {
      if (cancelled || pageUnloading || document.visibilityState === "hidden") return;
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch { /* keep silent */ }
    };
    const id = setInterval(tick, 8000);
    // Listen for global refresh events fired by pages after critical actions
    const onEvt = () => tick();
    window.addEventListener("m11:refresh-wallet", onEvt);
    window.addEventListener("m11:refresh", onEvt);
    // Also refresh on window/tab focus (user returning to app)
    const onFocus = () => {
      if (!pageUnloading && document.visibilityState === "visible") tick();
    };
    const onBeforeUnload = () => { pageUnloading = true; };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("beforeunload", onBeforeUnload, { capture: true });
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("m11:refresh-wallet", onEvt);
      window.removeEventListener("m11:refresh", onEvt);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("beforeunload", onBeforeUnload, { capture: true });
    };
  }, [userId, admin]);

  return (
    <AuthContext.Provider value={{ user, admin, login, register, adminLogin, logout, refreshUser, refresh: refreshUser, setSessionFromResetToken, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
